// OrderLineItems — the itemized services + pricing table for a quote, with each
// priced sub-line (a finishing op, or a line Nexus derived such as NCR pad glue
// or pallet prep) indented under its parent product line. Single source of truth
// for line-item rendering, shared by the configurator's "Your saved price" card
// and the checkout review step.
//
// Why sub-lines: Nexus prices each of them as its own line, so total_sale_amount
// includes them. The product lines alone would sum to LESS than the total;
// rendering each line plus its sub-lines makes the itemized view reconcile to the
// total.

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { QuoteDto } from 'shared';

import { formatCurrency, formatNumber } from '../i18n/formatters.js';

export function OrderLineItems({ quote }: { quote: QuoteDto }): JSX.Element | null {
  const { t } = useTranslation(['pricing', 'common']);
  if (quote.total_sale_amount == null) return null;
  const currency = quote.currency ?? 'USD';

  function money(value: string | null): string {
    if (value == null) return t('lineItems.priceUnavailable');
    const n = Number(value);
    return Number.isFinite(n) ? formatCurrency(n, currency) : t('lineItems.priceUnavailable');
  }

  return (
    <table>
      <thead>
        <tr>
          <th>{t('lineItems.table.item')}</th>
          <th style={{ textAlign: 'right' }}>{t('lineItems.table.quantity')}</th>
          <th style={{ textAlign: 'right' }}>{t('lineItems.table.unitPrice')}</th>
          <th style={{ textAlign: 'right' }}>{t('lineItems.table.subtotal')}</th>
        </tr>
      </thead>
      <tbody>
        {quote.lines.map((l, idx) => (
          <Fragment key={l.id}>
            <tr>
              <td>{l.title || t('lineItems.untitledItem', { number: idx + 1 })}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(l.quantity)}</td>
              <td style={{ textAlign: 'right' }}>{money(l.unit_price)}</td>
              <td style={{ textAlign: 'right' }}>{money(l.line_price)}</td>
            </tr>
            {l.finishing.map((f) => (
              // Indented sub-line at Nexus's own quantity for it, which need not be
              // the parent's (a pad count, a pallet count); a snapshot that never
              // stored one reads the parent's.
              <tr key={`${l.id}__${f.family_key}`}>
                <td style={{ paddingLeft: 24, opacity: 0.85 }}>
                  {t('lineItems.finishingSubline', { title: f.title })}
                </td>
                <td style={{ textAlign: 'right' }}>{formatNumber(f.quantity ?? l.quantity)}</td>
                <td style={{ textAlign: 'right' }}>{money(f.unit_price)}</td>
                <td style={{ textAlign: 'right' }}>{money(f.line_price)}</td>
              </tr>
            ))}
          </Fragment>
        ))}
        {quote.priced_lines.map((p) => (
          // Quote-level lines Nexus priced on their own (Pattern B): the rush
          // uplift, a physical proof, hands-on prep, the applied barcode. Rendered
          // top-level rather than indented — each applies to the whole order, not
          // to one project. Already in display order and already filtered to the
          // ones still priced, both at the data layer. Their line_price is part
          // of total_sale_amount, so rendering them is what keeps the itemization
          // reconciled to the total.
          <tr key={p.scope}>
            <td>{p.title}</td>
            <td style={{ textAlign: 'right' }}>{formatNumber(p.quantity)}</td>
            <td style={{ textAlign: 'right' }}>{money(p.unit_price)}</td>
            <td style={{ textAlign: 'right' }}>{money(p.line_price)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3} className="total">
            {t('lineItems.totalLabel')}
          </td>
          <td className="total">{money(quote.total_sale_amount)}</td>
        </tr>
      </tfoot>
    </table>
  );
}
